import type { ClientLobbyMsg, ServerLobbyMsg } from '@snakefeast/shared';
import { ensureAccessToken } from './api';
import { WS_URL } from './platform';

type Listener = (msg: ServerLobbyMsg) => void;
export type LobbyStatus = 'connecting' | 'open' | 'closed';

/**
 * Lobby connection with exponential-backoff reconnect (with jitter, so a
 * server restart doesn't get a thundering herd), offline/online awareness and
 * a send queue for messages issued while reconnecting.
 */
export class LobbySocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<(s: LobbyStatus) => void>();
  private queue: string[] = [];
  private attempt = 0;
  private stopped = true;
  private retryTimer: number | null = null;
  private pingTimer: number | null = null;
  rtt = 0;

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisible);
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.ws?.close(1000);
    this.ws = null;
    this.queue = [];
  }

  private onOnline = () => {
    if (!this.ws || this.ws.readyState > 1) this.reconnectNow();
  };
  private onVisible = () => {
    if (document.visibilityState === 'visible' && (!this.ws || this.ws.readyState > 1)) this.reconnectNow();
  };
  private reconnectNow() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.attempt = 0;
    void this.connect();
  }

  private emitStatus(s: LobbyStatus) {
    for (const l of this.statusListeners) l(s);
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.emitStatus('connecting');
    const token = await ensureAccessToken();
    if (!token) {
      this.scheduleRetry();
      return;
    }
    const ws = new WebSocket(`${WS_URL}/ws`, ['snakefeast.v1', `auth.${token}`]);
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.emitStatus('open');
      for (const m of this.queue.splice(0)) ws.send(m);
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.send({ t: 'ping', ts: performance.now() }), 15_000);
    };
    ws.onmessage = (e) => {
      let msg: ServerLobbyMsg;
      try {
        msg = JSON.parse(e.data as string) as ServerLobbyMsg;
      } catch {
        return;
      }
      if (msg.t === 'pong') this.rtt = performance.now() - msg.ts;
      for (const l of this.listeners) l(msg);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.emitStatus('closed');
      this.scheduleRetry();
    };
  }

  private scheduleRetry() {
    if (this.stopped) return;
    const delay = Math.min(15_000, 500 * 2 ** this.attempt++) * (0.6 + Math.random() * 0.8);
    this.retryTimer = window.setTimeout(() => void this.connect(), delay);
  }

  send(msg: ClientLobbyMsg): void {
    const raw = JSON.stringify(msg);
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(raw);
    else if (msg.t !== 'ping') this.queue.push(raw);
  }

  on(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  onStatus(l: (s: LobbyStatus) => void): () => void {
    this.statusListeners.add(l);
    return () => this.statusListeners.delete(l);
  }
}

export const lobbySocket = new LobbySocket();
